import { useCallback, useState } from 'react';
import type { PhotoScanResult } from '@nutrigeist/shared';
import { intakeApi } from '../api/endpoints.js';
import { ApiError } from '../api/client.js';
import { CameraCapture } from './CameraCapture.js';

interface PhotoScannerProps {
  onScanned: (result: PhotoScanResult) => void;
  onClose: () => void;
}

/**
 * Photographs a meal and suggests which foods it contains.
 *
 * Only names come from the photo. Estimating the portion was measured and does
 * not work — a reference Big Mac came out 197% too heavy, and four runs on one
 * unchanged image ranged 250-650 kcal — so the weight is asked for afterwards
 * rather than guessed. That is also why this shows a list to choose from: the
 * top guess is a suggestion, not an answer.
 */
export function PhotoScanner({ onScanned, onClose }: PhotoScannerProps): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (image: Blob) => {
      setBusy(true);
      setError(null);

      try {
        onScanned(await intakeApi.scanPhoto(image));
      } catch (caught) {
        setError(
          caught instanceof ApiError ? caught.message : 'Das Foto konnte nicht ausgewertet werden.',
        );
      } finally {
        setBusy(false);
      }
    },
    [onScanned],
  );

  return (
    <CameraCapture
      title="Mahlzeit fotografieren"
      hint="Fotografiere den Teller von schräg oben, sodass alle Bestandteile sichtbar sind. Die Menge gibst du danach selbst an."
      captureLabel="Aufnehmen"
      busyLabel="Wird erkannt…"
      busy={busy}
      error={error}
      onCapture={(image) => void submit(image)}
      onClose={onClose}
    />
  );
}
